import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import type { PhotoItem } from "@/content/photo-gallery";

type Props = {
  photos: PhotoItem[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
};

/**
 * Fullskärmsvisning av en bild med tangentbords- och swipenavigering.
 * Renderas bara på klienten (öppnas alltid av en användarinteraktion).
 */
export function PhotoLightbox({ photos, index, onClose, onIndexChange }: Props) {
  const open = index !== null;
  const touchX = useRef<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      const next = (index + delta + photos.length) % photos.length;
      setLoaded(false);
      onIndexChange(next);
    },
    [index, photos.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    }
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, go, onClose]);

  if (!open || index === null) return null;
  const photo = photos[index];
  if (!photo) return null;
  if (typeof document === "undefined") return null;
  const prevPhoto = photos.length > 1 ? photos[(index - 1 + photos.length) % photos.length] : null;
  const nextPhoto = photos.length > 1 ? photos[(index + 1) % photos.length] : null;


  // Portal till <body>: annars blir overlayen positionerad mot en animerad
  // förälder (transform skapar ny containing block) och hamnar fel i sidan.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      className="fixed inset-0 z-[120] flex items-center justify-center bg-background/95 backdrop-blur-xl"
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        const end = e.changedTouches[0]?.clientX ?? null;
        if (start !== null && end !== null && Math.abs(end - start) > 50) {
          go(end < start ? 1 : -1);
        }
        touchX.current = null;
      }}
    >
      <button
        type="button"
        aria-label="Stäng"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full border border-white/15 bg-surface/70 p-3 text-foreground transition hover:bg-surface"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Mörkade förhandsvisningar av föregående/nästa bild */}
      {prevPhoto ? (
        <button
          type="button"
          aria-label="Föregående bild"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          className="absolute left-0 top-1/2 hidden h-[52vh] w-[14vw] -translate-y-1/2 items-center justify-start overflow-hidden opacity-35 transition hover:opacity-70 md:flex"
        >
          <img
            src={prevPhoto.src}
            alt={prevPhoto.alt ?? "Föregående bild i galleriet"}
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full -translate-x-1/3 rounded-r-2xl object-cover brightness-[0.35] saturate-50"
          />
        </button>
      ) : null}
      {nextPhoto ? (
        <button
          type="button"
          aria-label="Nästa bild"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          className="absolute right-0 top-1/2 hidden h-[52vh] w-[14vw] -translate-y-1/2 items-center justify-end overflow-hidden opacity-35 transition hover:opacity-70 md:flex"
        >
          <img
            src={nextPhoto.src}
            alt={nextPhoto.alt ?? "Nästa bild i galleriet"}
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full translate-x-1/3 rounded-l-2xl object-cover brightness-[0.35] saturate-50"
          />
        </button>
      ) : null}

      <button
        type="button"
        aria-label="Föregående bild"
        onClick={(e) => {
          e.stopPropagation();
          go(-1);
        }}
        className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/15 bg-surface/70 p-3 text-foreground transition hover:bg-surface md:left-6"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Nästa bild"
        onClick={(e) => {
          e.stopPropagation();
          go(1);
        }}
        className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/15 bg-surface/70 p-3 text-foreground transition hover:bg-surface md:right-6"
      >
        <ChevronRight className="h-5 w-5" />
      </button>


      <figure
        className="mx-auto flex max-h-[88vh] w-full max-w-5xl flex-col items-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={photo.src}
          src={photo.src}
          width={photo.w}
          height={photo.h}
          alt={photo.alt}
          onLoad={() => setLoaded(true)}
          className={`max-h-[78vh] w-auto rounded-2xl object-contain shadow-2xl transition duration-500 ${
            loaded ? "opacity-100 blur-0" : "opacity-0 blur-sm"
          }`}
        />
        <figcaption className="mt-4 text-center text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {photo.alt} · Foto · {index + 1}/{photos.length}
        </figcaption>
      </figure>
    </div>,
    document.body,

  );
}
